/**
 * Dialog for adding a new question to a quiz
 */

import React, { useState } from 'react';
import './QuestionDialogs.css';
import { QuestionType } from '../db/schema';
import { MdBackspace } from 'react-icons/md';

interface AddQuestionDialogProps {
    isOpen: boolean;
    onClose: () => void;
    quizId: number;
}

export default function AddQuestionDialog({ isOpen, onClose, quizId }: AddQuestionDialogProps) {
    const [questionType, setQuestionType] = useState<QuestionType>(QuestionType.SHORT_ANSWER);
    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState('');
    const [choices, setChoices] = useState<string[]>(['', '']);
    const [answerExplanation, setAnswerExplanation] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);

    // Helper function to check if form has been modified
    const isFormModified = () => {
        if (question.trim() !== '') return true;
        if (questionType === QuestionType.SHORT_ANSWER && answer.trim() !== '') return true;
        if (questionType === QuestionType.MULTIPLE_CHOICE && choices.some(c => c.trim() !== '')) return true;
        if (questionType === QuestionType.TRUE_FALSE && answer !== 'True') return true;
        if (answerExplanation.trim() !== '') return true;
        return false;
    };

    // Function to clear all form state
    const clearFormState = () => {
        setQuestionType(QuestionType.SHORT_ANSWER);
        setQuestion('');
        setAnswer('');
        setChoices(['', '']);
        setAnswerExplanation('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // For multiple choice, the answer is the first choice
        const finalAnswer = questionType === QuestionType.MULTIPLE_CHOICE
            ? choices[0] || ''
            : answer;

        if (question.trim() && finalAnswer.trim()) {
            try {
                const answerChoices = questionType === QuestionType.MULTIPLE_CHOICE
                    ? choices.filter(c => c.trim() !== '')
                    : [];

                const response = await fetch(`/api/quizzes/${quizId}/questions`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        question,
                        answer: finalAnswer,
                        questionType,
                        answerChoices,
                        answerExplanation: answerExplanation.trim() || null
                    }),
                });

                if (response.ok) {
                    clearFormState();
                    onClose();
                }
            } catch (error) {
                console.error('Failed to add question:', error);
            }
        }
    };

    const handleChoiceChange = (index: number, value: string) => {
        const newChoices = [...choices];
        newChoices[index] = value;
        setChoices(newChoices);
    };

    const handleAddChoice = () => {
        setChoices([...choices, '']);
    };

    const handleRemoveChoice = (index: number) => {
        // Don't allow removing if there are only 2 choices left
        if (choices.length <= 2) return;
        const newChoices = choices.filter((_, i) => i !== index);
        setChoices(newChoices);
    };

    const handleQuestionTypeChange = (type: QuestionType) => {
        setQuestionType(type);
        // Reset answer when switching types
        if (type === QuestionType.TRUE_FALSE) {
            setAnswer('True');
        } else {
            setAnswer('');
        }
    };

    const handleGenerateWithAI = async () => {
        if (question.trim().split(/\s+/).length < 3) {
            return; // Need at least 3 words
        }

        setIsGenerating(true);

        try {
            const numWrongAnswers = questionType === QuestionType.MULTIPLE_CHOICE
                ? choices.length - 1  // Exclude the correct answer
                : 0;

            const response = await fetch('/api/ai/generate-answer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    question,
                    questionType,
                    numWrongAnswers,
                }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to generate answer');
            }

            const data = await response.json();

            // Update the answer
            if (questionType === QuestionType.MULTIPLE_CHOICE && data.wrongAnswers) {
                // Set the correct answer as the first choice
                const newChoices = [data.answer, ...data.wrongAnswers];
                setChoices(newChoices);
            } else {
                setAnswer(data.answer);
            }

            // Update the explanation
            setAnswerExplanation(data.explanation || '');

        } catch (error) {
            console.error('Failed to generate AI answer:', error);
            alert(error instanceof Error ? error.message : 'Failed to generate answer. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    // Handle close with confirmation
    const handleClose = () => {
        if (isFormModified()) {
            if (window.confirm('You have unsaved changes. Are you sure you want to close? The question form state will be cleared.')) {
                clearFormState();
                onClose();
            }
        } else {
            clearFormState();
            onClose();
        }
    };

    // Handle clear button click
    const handleClear = () => {
        if (window.confirm('Are you sure you want to clear the form? All entered data will be lost.')) {
            clearFormState();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="overlay" onClick={handleOverlayClick}>
            <div className="dialog">
                <div className="header">
                    <h2 className="title">Add New Question</h2>
                    <button className="closeButton" onClick={handleClose} type="button">
                        ×
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="form">
                    <div className="questionTypeSelector">
                        <select
                            className="questionTypeSelect"
                            value={questionType}
                            onChange={(e) => handleQuestionTypeChange(Number(e.target.value) as QuestionType)}
                        >
                            <option value={QuestionType.SHORT_ANSWER}>Short Answer</option>
                            <option value={QuestionType.MULTIPLE_CHOICE}>Multiple Choice</option>
                            <option value={QuestionType.TRUE_FALSE}>True/False</option>
                        </select>
                    </div>

                    <div className="formGroup">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label htmlFor="question" className="label">
                                Question:
                            </label>
                            <button
                                type="button"
                                className="aiButton"
                                onClick={handleGenerateWithAI}
                                disabled={isGenerating || question.trim().split(/\s+/).length < 3}
                                style={{
                                    fontSize: '0.875rem',
                                    padding: '0.375rem 0.75rem',
                                    backgroundColor: '#4f46e5',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    cursor: isGenerating || question.trim().split(/\s+/).length < 3 ? 'not-allowed' : 'pointer',
                                    opacity: isGenerating || question.trim().split(/\s+/).length < 3 ? 0.5 : 1,
                                }}
                            >
                                {isGenerating ? 'Generating...' : 'Answer with AI'}
                            </button>
                        </div>
                        <textarea
                            id="question"
                            name="question"
                            className="textarea"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="Enter your question here..."
                            required
                        />
                    </div>

                    {questionType === QuestionType.SHORT_ANSWER && (
                        <div className="formGroup">
                            <label htmlFor="answer" className="label">
                                Answer:
                            </label>
                            <input
                                id="answer"
                                name="answer"
                                type="text"
                                className="input"
                                value={answer}
                                onChange={(e) => setAnswer(e.target.value)}
                                placeholder="Enter the correct answer..."
                                required
                            />
                        </div>
                    )}

                    {questionType === QuestionType.MULTIPLE_CHOICE && (
                        <div className="formGroup">
                            <div className="choicesHeader">
                                <label className="label">Answer Choices:</label>
                                <button
                                    type="button"
                                    className="addChoiceLink"
                                    onClick={handleAddChoice}
                                >
                                    Add choice
                                </button>
                            </div>
                            {choices.map((choice, index) => (
                                <div key={index} className="choiceRow">
                                    <label className="choiceLabel">
                                        {index === 0 ? "Correct" : "Incorrect"}
                                    </label>
                                    <input
                                        type="text"
                                        className="input choiceInput"
                                        value={choice}
                                        onChange={(e) => handleChoiceChange(index, e.target.value)}
                                        placeholder={index === 0 ? "Enter correct answer" : "Enter an incorrect choice"}
                                        required={index < 2}
                                    />
                                    {index > 0 && choices.length > 2 && (
                                        <button
                                            type="button"
                                            className="removeChoiceButton"
                                            onClick={() => handleRemoveChoice(index)}
                                            title="Remove choice"
                                        >
                                            <MdBackspace />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {questionType === QuestionType.TRUE_FALSE && (
                        <div className="formGroup">
                            <label className="label">Correct Answer:</label>
                            <div className="trueFalseButtons">
                                <button
                                    type="button"
                                    className={`trueFalseButton ${answer === 'True' ? 'selected' : ''}`}
                                    onClick={() => setAnswer('True')}
                                >
                                    True
                                </button>
                                <button
                                    type="button"
                                    className={`trueFalseButton ${answer === 'False' ? 'selected' : ''}`}
                                    onClick={() => setAnswer('False')}
                                >
                                    False
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="formGroup">
                        <label htmlFor="answerExplanation" className="label">
                            Answer Explanation (optional):
                        </label>
                        <textarea
                            id="answerExplanation"
                            name="answerExplanation"
                            className="textarea"
                            value={answerExplanation}
                            onChange={(e) => setAnswerExplanation(e.target.value)}
                            placeholder="Explain why this is the correct answer..."
                            rows={3}
                        />
                    </div>

                    <div className="buttonGroup">
                        <button type="button" className="cancelButton" onClick={handleClear}>
                            Clear
                        </button>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem' }}>
                            <button type="button" className="cancelButton" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="submitButton">
                                Add Question
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
